import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';

import ExitingPersistence from '@atlaskit/motion/exiting-persistence';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import { Panel } from '@atlaskit/navigation-system/layout/panel';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';

import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api/use-preview-panel-actions';

import { fg } from '@confluence/feature-gating';

import { messages } from './messages.ts';

const PreviewPanelSplitter = () => {
	const intl = useIntl();
	const { setPanelWidth } = usePreviewPanelsActions();

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			void setPanelWidth(finalWidth);
		},
		[setPanelWidth],
	);

	return (
		<PanelSplitter label={intl.formatMessage(messages.panelSplitter)} onResizeEnd={onResizeEnd} />
	);
};

const NavWrapper = ({
	children,
	width,
}: {
	children: React.ReactElement;
	width: number | undefined;
}) => (
	<Panel defaultWidth={width}>
		<PreviewPanelSplitter />
		{children}
	</Panel>
);

export function PreviewPanelContainer(): React.JSX.Element | null {
	const panel = useCurrentPreviewPanel();

	// When there is no active panel we still want the ExitingPersistence wrapper (below) to remain
	// mounted so the Panel's built-in exit motion can play before it is removed from the DOM.
	const PanelContent = panel?.content ?? null;
	const previewPanel =
		panel != null && PanelContent != null ? (
			<NavWrapper width={panel.width}>
				<FlagsProvider>
					<PanelContent />
				</FlagsProvider>
			</NavWrapper>
		) : null;

	// The ADS Panel only animates its enter/exit (via its internal useMotion hook) when the
	// `platform-dst-motion-uplift-panel` gate is enabled. Behind the same gate we keep an
	// always-mounted ExitingPersistence and render the Panel conditionally inside it so the exit
	// motion can play before the Panel unmounts. With the gate off we keep the previous behaviour of
	// returning null when there is no active panel.
	if (fg('platform-dst-motion-uplift-panel')) {
		return <ExitingPersistence>{previewPanel}</ExitingPersistence>;
	}

	return previewPanel;
}
